import { Icon } from '@magicschool/spellbook/components/Icon';
import type { PaginationProps, PaginationText } from '@magicschool/spellbook/components/Pagination';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import { Text } from '@magicschool/spellbook/components/Text';
import { toggleButtonVariants } from '@magicschool/spellbook/components/ToggleButton';
import { useDynamicTextResolver } from '@magicschool/spellbook/hooks/useDynamicTextResolver';
import { cn } from '@magicschool/spellbook/lib/utils';

type PaginationControlProps = {
  isActive?: boolean;
  disabled?: PaginationProps['disabled'];
  isLoading?: PaginationProps['isLoading'];
  text?: PaginationText;
  onPageChange?: PaginationProps['onPageChange'];
  page: number;
};

export const PaginationButtonSlot = Slot<React.ComponentProps<'button'> & PaginationControlProps>({
  displayName: 'Pagination.Button',
  preventOverride: true,
  renderDefault: ({ children, className, isActive, disabled, isLoading, onClick, onPageChange, text, page, ...props }) => {
    const resolveGoToPage = useDynamicTextResolver(text?.goToPage, (pageNum: number) => `Go to page ${pageNum}`);

    if (!page) {
      throw new Error('PaginationButtonSlot requires a "page" prop to be defined.');
    }

    const isActuallyDisabled = disabled || isLoading;

    const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isActuallyDisabled) {
        // explicitly prevent default and stop propagation
        event.preventDefault();
        event.stopPropagation();

        return;
      }

      onClick?.(event);
      !isActive && onPageChange?.();
    };

    return (
      <button
        data-spellbook-name="pagination-button"
        type="button"
        className={cn(
          toggleButtonVariants({ variant: 'default', size: 'md', shape: 'default', color: 'primary', withBorder: true }),
          'min-w-9',
          className,
        )}
        {...(isActive ? { 'aria-current': 'page', 'data-state': 'on' } : { 'data-state': 'off' })}
        onClick={handleOnClick}
        aria-disabled={isActuallyDisabled}
        aria-busy={isLoading}
        aria-label={resolveGoToPage(page)}
        {...props}
      >
        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          <Text variant="label-1" className="px-1" element="span">
            {children ?? page}
          </Text>
        )}
      </button>
    );
  },
});

export const PaginationPrevButtonSlot = Slot<
  React.ComponentProps<'button'> & {
    iconOnly?: boolean;
    isLoading?: boolean;
    text?: PaginationText;
    onPageChange?: PaginationControlProps['onPageChange'];
  }
>({
  displayName: 'Pagination.PrevButton',
  preventOverride: true,
  renderDefault: ({ children, className, iconOnly, isLoading, disabled, onClick, onPageChange, text, ...props }) => {
    const defaultLabel = 'Previous Page';
    const goToPrevPageText = text?.goToPrevPage ?? 'Go to previous page';

    const isActuallyDisabled = disabled || isLoading;

    const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isActuallyDisabled) {
        // explicitly prevent default and stop propagation
        event.preventDefault();
        event.stopPropagation();

        return;
      }

      onClick?.(event);
      onPageChange?.();
    };

    return (
      <button
        data-spellbook-name="pagination-prev-button"
        type="button"
        className={cn(
          toggleButtonVariants({ variant: 'default', size: 'md', shape: 'default', color: 'primary', withBorder: true }),
          'min-w-9',
          className,
        )}
        onClick={handleOnClick}
        aria-disabled={isActuallyDisabled}
        aria-busy={isLoading}
        aria-label={goToPrevPageText}
        {...props}
      >
        {isLoading ? <Spinner size="md" /> : <Icon name="chevron-left" className="rtl:rotate-180" size="sm" />}
        {iconOnly ? (
          <span className="sr-only">{children || defaultLabel}</span>
        ) : (
          <Text variant="label-1" className="px-1 sr-only md:not-sr-only" element="span">
            {children || defaultLabel}
          </Text>
        )}
      </button>
    );
  },
});

export const PaginationNextButtonSlot = Slot<
  React.ComponentProps<'button'> & {
    iconOnly?: boolean;
    isLoading?: boolean;
    text?: PaginationText;
    onPageChange?: PaginationControlProps['onPageChange'];
  }
>({
  displayName: 'Pagination.NextButton',
  preventOverride: true,
  renderDefault: ({ children, className, iconOnly, disabled, onClick, isLoading, onPageChange, text, ...props }) => {
    const defaultLabel = 'Next Page';
    const goToNextPageText = text?.goToNextPage ?? 'Go to next page';
    const isActuallyDisabled = disabled || isLoading;

    const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isActuallyDisabled) {
        // explicitly prevent default and stop propagation
        event.preventDefault();
        event.stopPropagation();

        return;
      }

      onClick?.(event);
      onPageChange?.();
    };

    return (
      <button
        data-spellbook-name="pagination-next-button"
        type="button"
        className={cn(
          toggleButtonVariants({ variant: 'default', size: 'md', shape: 'default', color: 'primary', withBorder: true }),
          'min-w-9',
          className,
        )}
        onClick={handleOnClick}
        aria-disabled={isActuallyDisabled}
        aria-label={goToNextPageText}
        aria-busy={isLoading}
        {...props}
      >
        {iconOnly ? (
          <span className="sr-only">{children || defaultLabel}</span>
        ) : (
          <Text variant="label-1" className="px-1 sr-only md:not-sr-only" element="span">
            {children || defaultLabel}
          </Text>
        )}
        {isLoading ? <Spinner size="sm" /> : <Icon name="chevron-right" className="rtl:rotate-180" size="sm" />}
      </button>
    );
  },
});

export const PaginationEllipsisSlot = Slot<React.ComponentProps<'span'>>({
  displayName: 'Pagination.Ellipsis',
  preventOverride: true,
  renderDefault: ({ className, ...props }) => (
    <span
      data-spellbook-name="pagination-ellipsis"
      className={cn(
        [
          'size-control-h-sm',
          'gap-0',
          'px-2',
          'py-2',
          'inline-flex',
          'items-center',
          'justify-center',
          'whitespace-nowrap',
          'transition-colors',
          'shrink-0',
          '[&_svg]:shrink-0',
        ],
        className,
      )}
      {...props}
    >
      <Icon name="ellipsis" color="default" size="sm" />
    </span>
  ),
});
